import React, { useContext } from 'react'
import CustTable from '../../Common/CustTable'
import { t } from 'i18next'
import { Button, Col, Popover, Row } from 'antd'
import { FormOutlined, UserOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import Info from '../../Common/Info'
import { post } from '../../Common/api'
import GlobalContext from '../GlobalContext'
import StateTag from '../TagsPage/StateTag'
import SlotTypeTag from '../TagsPage/SlotTypeTag'

function SlotRangeWithNextSlot ({ slot, allSlots }) {
  const rangeText = `${dayjs(slot?.begin_date).format('H[h]mm')}-${dayjs(slot?.end_date).format('H[h]mm')}`
  const nextSlot = allSlots
    .filter(s => s.box_id === slot.box_id && dayjs(s.begin_date).isAfter(dayjs(slot.begin_date)) && !s.is_cancel)
    .sort((a, b) => dayjs(a.begin_date) - dayjs(b.begin_date))[0]

  if (!nextSlot) {
    return (
      <Popover title={t('Admin.SlotList.table.nextSlotEmpty')}>
        <span style={{ cursor: 'pointer' }}>{rangeText}</span>
      </Popover>
    )
  }

  const popoverContent = (
    <div style={{ width: '20em' }}>
      <Row><Col span={8}><strong>{t('Admin.SlotList.table.name')}</strong></Col><Col span={16}>{nextSlot.name}</Col></Row>
      <Row><Col span={8}><strong>{t('Admin.SlotList.table.range')}</strong></Col><Col span={16}>{dayjs(nextSlot.begin_date).format('HH[h]mm')}</Col></Row>
      <Row><Col span={8}><strong>{t('Admin.SlotList.table.phone')}</strong></Col><Col span={16}>+{nextSlot.phone}</Col></Row>
      <Row style={{ marginTop: '0.5em' }}><Col span={24}><Link to={`/admin/slots/${nextSlot.id}?from=list`}><Button size="small" icon={<FormOutlined />}>{t('Admin.SlotList.table.viewSlot')}</Button></Link></Col></Row>
    </div>
  )

  return (
    <Popover content={popoverContent} title={t('Admin.SlotList.table.nextSlot')}>
      <span style={{ cursor: 'pointer' }}>{rangeText}</span>
    </Popover>
  )
}

export default function SlotList ({ filteredSlots, setLoading, updateSlots }) {
  const screens = useBreakpoint()
  const { user, notificationApi, slots, boxes, slotsDate } = useContext(GlobalContext)
  const daySlots = slots.filter((slot) => dayjs(slot.begin_date).format('YYYY-MM-DD') === slotsDate.format('YYYY-MM-DD'))

  const assign = (id) => {
    setLoading(true)
    post({
      url: `admin/slots/${id}/assign`,
      params: {},
      notification: { api: notificationApi, state: 'update' },
      onSuccess: (json) => {
        updateSlots(json)
        setLoading(false)
      },
      onFailure: () => {
        setLoading(false)
      }
    })
  }

  const columns = (screens, boxes) => [
    {
      title: t('Admin.SlotList.table.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: t('Admin.SlotList.table.age'),
      dataIndex: 'age',
      key: 'age',
      width: 60
    },
    {
      title: <Info text={t('Admin.SlotList.table.bookedSlots')} infoText={t('Admin.SlotList.table.infoBookedSlots')} />,
      dataIndex: 'booked_slots',
      key: 'booked_slots',
      width: 110,
      render: (_, o) => o.booked_slots
    },
    {
      title: t('Admin.SlotList.table.box'),
      dataIndex: 'box.name',
      key: 'box.name',
      render: (_, o) => {
        const box = boxes.find(box => box.id === o.box_id)
        return `${box.name} - ${box.interphone}`
      },
      width: 200,
      sorter: (a, b) => {
        const boxA = boxes.find(box => box.id === a.box_id)
        const boxB = boxes.find(box => box.id === b.box_id)
        return boxA.name.localeCompare(boxB.name)
      }
    },
    {
      title: t('Admin.SlotList.table.assigned'),
      dataIndex: 'assigned_short_name',
      key: 'assigned_short_name',
      width: 100,
      sorter: (a, b) => a.assigned_short_name.localeCompare(b.assigned_short_name),
      render: (_, o) => o.assigned_short_name
    },
    {
      width: 80,
      title: t('Admin.SlotList.table.slotType'),
      dataIndex: 'type',
      key: 'type',
      render: (_, o) => <SlotTypeTag type={o.type} />,
      sorter: (a, b) => a?.type.localeCompare(b?.type),
      hidden: !screens.md
    },
    {
      width: 100,
      title: t('Admin.SlotList.table.range'),
      dataIndex: 'range',
      key: 'range',
      render: (_, o) => <SlotRangeWithNextSlot slot={o} allSlots={daySlots} />,
      sorter: (a, b) => dayjs(a.begin_date) - dayjs(b.begin_date)
    },
    {
      width: 100,
      title: t('Admin.SlotList.table.phone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => `+${phone}`,
      sorter: (a, b) => a.phone - b.phone
    },
    {
      width: 160,
      title: t('Admin.SlotList.table.state'),
      dataIndex: 'state',
      key: 'state',
      render: (_, o) => <StateTag slot={o} boxes={boxes} />,
      sorter: (a, b) => a?.state.localeCompare(b?.state)
    },
    {
      width: 70,
      title: t('Admin.SlotList.table.digicode'),
      dataIndex: 'digicode',
      key: 'digicode',
      sorter: (a, b) => a?.digicode.localeCompare(b?.digicode),
      hidden: !screens.md
    },
    {
      width: 80,
      key: 'action',
      render: (_, o) => <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <Link to={`/admin/slots/${o.id}?from=list`}><Button shape="circle" icon={<FormOutlined />} /></Link>
        { o.assigned_id !== user.id &&
          <Button shape="circle" icon={<UserOutlined />} onClick={() => assign(o.id)} />
        }
      </div>
    }
  ].filter((col) => !col.hidden)

  return (
    <CustTable
      height={600}
      initColumns={columns(screens, boxes)}
      dataSource={filteredSlots}
    />
  )
}
